import React, { useState, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { Eye, EyeOff, CheckCircle2, XCircle, Circle } from "lucide-react";
import { apiClient } from "../../services/apiClientService";
import toast from "react-hot-toast";
import OtpInput from "../common/OtpInput";
import Lottie from "lottie-react";
import { useUserAuth } from "../../context/UserAuthContext";
import { isValidEmail } from "../../utils/validators";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import {
  formatIndianPhoneGroups,
  isValidIndianPhoneDigits,
  normalizeIndianPhoneDigits,
} from "../../utils/phoneFormat";
import {
  getPasswordChecks,
  passwordMeetsMinimum,
  STRENGTH_META,
  strengthScore,
} from "../../utils/passwordChecks";
import {
  trackUserLoggedIn,
  trackUserSignedUp,
} from "../../analytics/events";

import loadingAnimation from "../../assets/images/Login.json";

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AUTH_INPUT =
  "w-full rounded-xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-900 shadow-sm placeholder:text-stone-400 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20";

const AUTH_LABEL = "mb-1.5 block text-xs font-semibold text-stone-600";

function isUserAlreadyExistsError(message) {
  const s = String(message || "").toLowerCase();
  return s.includes("already exist");
}

const UserAuthForm = () => {
  const [mode, setMode] = useState("login");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otp, setOtp] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailFieldReady, setEmailFieldReady] = useState(false);
  const [showExistsModal, setShowExistsModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwTouched, setPwTouched] = useState(false);

  const checks = useMemo(() => getPasswordChecks(formData.password), [formData.password]);
  const score = useMemo(() => strengthScore(checks), [checks]);
  const strengthMeta = score > 0 ? STRENGTH_META[score] : null;
  const confirmMatch =
    formData.confirmPassword.length > 0 && formData.password === formData.confirmPassword;
  const confirmMismatch =
    formData.confirmPassword.length > 0 && formData.password !== formData.confirmPassword;

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useUserAuth();
  const { googleLoginUserEnabled } = useFeatureFlags();

  const redirectTarget = (() => {
    const from = location.state?.from;
    // Sometimes we pass a string ("/checkout"), sometimes a location object.
    if (typeof from === "string" && from.trim()) return from;
    if (from && typeof from === "object" && from.pathname) {
      return `${from.pathname || ""}${from.search || ""}${from.hash || ""}`;
    }
    return "/shop";
  })();

  const handleGoogleSuccess = useCallback(
    async (tokenResponse) => {
      try {
        const data = await apiClient("POST", "/api/users/google-login", {
          token: tokenResponse.access_token,
        });
        if (data?.user) {
          login(data.user, data.token);
        } else {
          const profile = await apiClient("GET", "/api/users/profile", null, {
            skipLoading: true,
          });
          if (profile?.email) login(profile);
        }
        trackUserLoggedIn({ method: "google" });
        navigate(redirectTarget, { replace: true });
      } catch (error) {
        toast.error(error?.message || "Google login failed");
        console.error(error);
      }
    },
    [login, navigate, redirectTarget],
  );

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => toast.error("Google login failed"),
    flow: "implicit",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "password") setPwTouched(true);
  };

  const handleEmailBlur = () => {
    setEmailTouched(true);
  };

  const enableEmailField = (e) => {
    setEmailFieldReady(true);
    e.target.removeAttribute("readonly");
  };

  const emailValid = isValidEmail(formData.email);
  const showEmailFeedback = emailTouched && formData.email.length > 0;

  const handlePhoneChange = (e) => {
    const digits = normalizeIndianPhoneDigits(e.target.value);
    setFormData((prev) => ({ ...prev, phone: digits }));
  };

  const switchToLogin = () => {
    setShowExistsModal(false);
    setMode("login");
    setShowOtpInput(false);
    setPwTouched(false);
    setEmailTouched(false);
    setEmailFieldReady(false);
    setFormData((prev) => ({
      ...prev,
      confirmPassword: "",
      password: "",
      phone: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailTouched(true);
    try {
      if (!isValidEmail(formData.email)) {
        toast.error("Please enter a valid email address.");
        return;
      }
      if (mode === "signup") {
        if (!passwordMeetsMinimum(checks)) {
          toast.error("Password does not meet the minimum requirements.");
          return;
        }
        if (formData.password !== formData.confirmPassword) {
          toast.error("Passwords do not match.");
          return;
        }
        if (!isValidIndianPhoneDigits(formData.phone)) {
          toast.error("Please enter a valid 10-digit mobile number.");
          return;
        }
        await apiClient("POST", "/api/users/signup-send-otp", {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone,
        });
        toast.success("Signup successful. OTP sent to your email.");
        setShowOtpInput(true);
      } else {
        const data = await apiClient("POST", "/api/users/login", {
          email: formData.email,
          password: formData.password,
        });
        if (data?.user) {
          login(data.user, data.token);
        } else {
          const profile = await apiClient("GET", "/api/users/profile", null, {
            skipLoading: true,
          });
          if (profile?.email) login(profile);
        }
        trackUserLoggedIn({ method: "email" });
        toast.success("Login successful");
        navigate(redirectTarget, { replace: true });
      }
    } catch (error) {
      const msg = error?.message || "Something went wrong";
      if (mode === "signup" && isUserAlreadyExistsError(msg)) {
        setShowExistsModal(true);
        return;
      }
      toast.error(msg);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setShowOtpInput(false);
    setPwTouched(false);
    setEmailTouched(false);
    setEmailFieldReady(false);
    setFormData((prev) => ({
      ...prev,
      confirmPassword: "",
      ...(next === "login" ? { password: "", phone: "" } : {}),
    }));
  };

  const handleOtpVerify = async (e) => {
    e.preventDefault();
    try {
      if (!isValidEmail(formData.email)) {
        toast.error("Please enter a valid email address.");
        return;
      }
      await apiClient("POST", "/api/users/verify-signup-otp", {
        email: formData.email,
        otp,
      });
      trackUserSignedUp({ method: "email" });
      toast.success("Email verified successfully! Please log in.");
      setMode("login");
      setShowOtpInput(false);
    } catch (err) {
      const msg = err?.message || "OTP verification failed";
      if (isUserAlreadyExistsError(msg)) {
        setShowExistsModal(true);
        return;
      }
      toast.error(msg);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-orange-50/90 via-stone-50 to-stone-100 px-4 py-10">
      <div className="w-full max-w-[920px] overflow-hidden rounded-3xl border border-stone-200/90 bg-white shadow-xl shadow-stone-200/50 ring-1 ring-black/[0.03] flex flex-col md:flex-row">
        {/* Brand panel */}
        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-orange-500 via-orange-500 to-amber-500 p-8 text-white md:flex md:w-[42%]">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-orange-100">Welcome</p>
            <h1 className="mt-2 text-2xl font-bold leading-tight">Shop local with one account</h1>
            <p className="mt-3 text-sm text-orange-50/95">Track orders, save addresses, check out faster.</p>
          </div>
          <Lottie
            animationData={loadingAnimation}
            loop
            autoplay
            className="mx-auto mt-8 w-full max-w-[200px] opacity-90"
          />
        </div>

        {/* --- Form Section --- */}
        <div className="w-full p-6 sm:p-8 md:w-[58%] md:p-10">
          {!showOtpInput && (
            <div className="mb-6 flex rounded-xl bg-stone-100 p-1">
              <button
                type="button"
                onClick={() => switchMode("login")}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition ${
                  mode === "login" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
                }`}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => switchMode("signup")}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition ${
                  mode === "signup" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"
                }`}
              >
                Sign up
              </button>
            </div>
          )}
          <h2 className="mb-1 text-2xl font-bold text-stone-900">
            {showOtpInput
              ? "Verify your email"
              : mode === "login"
              ? "Welcome back"
              : "Create your account"}
          </h2>
          <p className="mb-6 text-sm text-stone-600">
            {showOtpInput
              ? `Enter the code we sent to ${formData.email}`
              : mode === "login"
              ? "Sign in to continue shopping."
              : "Quick signup — required fields marked below."}
          </p>

          {showOtpInput ? (
            <form onSubmit={handleOtpVerify} className="space-y-6">
              <OtpInput length={6} onChange={(value) => setOtp(value)} />
              <button
                type="submit"
                className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 py-3 text-sm font-bold text-white shadow-md hover:from-orange-600 hover:to-amber-600 transition"
              >
                Verify Email
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                {mode === "signup" && (
                  <>
                    <input
                      type="text"
                      name="name"
                      placeholder="Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      className={AUTH_INPUT}
                      required
                    />
                    <div className="flex w-full overflow-hidden rounded-xl border border-stone-200 focus-within:ring-2 focus-within:ring-orange-400/20">
                      <span className="flex shrink-0 items-center border-r border-gray-200 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-600">
                        +91
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        placeholder="--- --- ----"
                        value={formatIndianPhoneGroups(formData.phone)}
                        onChange={handlePhoneChange}
                        className="min-w-0 flex-1 border-0 bg-white px-4 py-2 text-base outline-none sm:text-sm"
                        required
                      />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <input
                    type="email"
                    name="email"
                    id="mmd-customer-email"
                    autoComplete={mode === "signup" ? "email" : "username"}
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    onFocus={enableEmailField}
                    readOnly={!emailFieldReady}
                    className={`${AUTH_INPUT} ${
                      showEmailFeedback
                        ? emailValid
                          ? "border-emerald-400 focus:ring-emerald-300"
                          : "border-rose-400 focus:ring-rose-300"
                        : ""
                    }`}
                    required
                  />
                  {showEmailFeedback && emailValid && (
                    <p className="flex items-center gap-1 text-xs text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Valid email
                    </p>
                  )}
                  {showEmailFeedback && !emailValid && (
                    <p className="flex items-center gap-1 text-xs text-rose-600">
                      <XCircle className="h-3.5 w-3.5 shrink-0" /> Invalid email
                    </p>
                  )}
                </div>

                {mode === "signup" ? (
                  <>
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          autoComplete="new-password"
                          placeholder="Create a password"
                          value={formData.password}
                          onChange={handleChange}
                          className={`${AUTH_INPUT} pr-11`}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowPassword((s) => !s)}
                          tabIndex={-1}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {pwTouched && formData.password.length > 0 && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                              <div
                                className={`h-full rounded-full transition-all duration-300 ${strengthMeta?.color ?? ""} ${strengthMeta?.bar ?? "w-0"}`}
                              />
                            </div>
                            {strengthMeta && (
                              <span
                                className={`text-xs font-semibold whitespace-nowrap ${strengthMeta.text}`}
                              >
                                {strengthMeta.label}
                              </span>
                            )}
                          </div>
                          <ul className="space-y-0.5">
                            {checks.map((c) => (
                              <li key={c.id} className="flex items-center gap-1.5 text-xs">
                                {c.pass ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                                ) : (
                                  <Circle className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                                )}
                                <span className={c.pass ? "text-emerald-700" : "text-gray-500"}>
                                  {c.label}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      <div className="relative">
                        <input
                          type={showConfirm ? "text" : "password"}
                          name="confirmPassword"
                          autoComplete="new-password"
                          placeholder="Confirm password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          className={`${AUTH_INPUT} pr-11 ${
                            confirmMismatch
                              ? "border-rose-400 focus:ring-rose-300"
                              : confirmMatch
                                ? "border-emerald-400 focus:ring-emerald-300"
                                : ""
                          }`}
                          required
                        />
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          onClick={() => setShowConfirm((s) => !s)}
                          tabIndex={-1}
                          aria-label={showConfirm ? "Hide password" : "Show password"}
                        >
                          {showConfirm ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {confirmMismatch && (
                        <p className="flex items-center gap-1 text-xs text-rose-600">
                          <XCircle className="h-3.5 w-3.5 shrink-0" /> Passwords do not match
                        </p>
                      )}
                      {confirmMatch && (
                        <p className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Passwords match
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={AUTH_INPUT}
                    required
                  />
                )}

                <button
                  type="submit"
                  className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 py-3 text-sm font-bold text-white shadow-md hover:from-orange-600 hover:to-amber-600 transition active:scale-[0.99]"
                >
                  {mode === "login" ? "Login" : "Sign Up"}
                </button>
              </form>

              {googleClientId && googleLoginUserEnabled ? (
                <>
                  <div className="flex items-center gap-3 my-4">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">or</span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <button
                    type="button"
                    onClick={() => googleLogin()}
                    className="flex w-full items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 hover:shadow active:scale-[0.98]"
                  >
                    <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
                      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                    </svg>
                    Continue with Google
                  </button>
                </>
              ) : null}

            </>
          )}
        </div>
      </div>

      {showExistsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="user-exists-title"
        >
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
            <h2 id="user-exists-title" className="text-lg font-bold text-gray-900">
              Account already exists
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              An account with{" "}
              <span className="font-semibold text-gray-900">{formData.email}</span> is already
              registered. Log in to continue.
            </p>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setShowExistsModal(false)}
                className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={switchToLogin}
                className="rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-2.5 text-sm font-semibold text-white hover:from-orange-600 hover:to-amber-600"
              >
                Continue to login
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAuthForm;
